import { eventBus } from '@spon/plugins';
import { safeRefresh } from '@/utils/safeRefresh';

// Touch / small-viewport check — Lenis interferes with native momentum
// scrolling on mobile and the runtime cost isn't justified there.
const isTouchOrMobile = () =>
  window.matchMedia('(hover: none), (max-width: 1023px)').matches;

function SmoothScroll() {
  if (isTouchOrMobile()) {
    return () => {};
  }

  let lenis = null;
  let gsapRef = null;
  let ScrollTriggerRef = null;

  function rafCallback(time) {
    lenis.raf(time * 1000);
  }

  async function init() {
    const [{ default: Lenis }, { default: gsap }, { ScrollTrigger }] =
      await Promise.all([
        import('lenis'),
        import('gsap'),
        import('gsap/ScrollTrigger'),
      ]);

    gsap.registerPlugin(ScrollTrigger);
    gsapRef = gsap;
    ScrollTriggerRef = ScrollTrigger;

    lenis = new Lenis({
      lerp: 0.1,
      smoothWheel: true,
    });

    // Sync Lenis with GSAP ScrollTrigger
    lenis.on('scroll', ScrollTrigger.update);

    // Share scroll position via eventBus (PixelCanvas reads this)
    lenis.on('scroll', ({ scroll }) => {
      eventBus._smoothScrollTop = scroll;
    });

    // Drive Lenis from GSAP's ticker for frame-perfect sync
    gsap.ticker.add(rafCallback);
    gsap.ticker.lagSmoothing(0);
  }

  function reset() {
    // Keep native scroll position and Lenis internal state in sync.
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    if (lenis) {
      // force:true makes the jump apply even if Lenis is currently stopped.
      lenis.scrollTo(0, { immediate: true, force: true });
    }
  }

  function pause() {
    if (lenis) lenis.stop();
  }

  function resume() {
    if (lenis) lenis.start();
  }

  function destroy() {
    if (lenis && gsapRef) {
      gsapRef.ticker.remove(rafCallback);
      lenis.destroy();
      lenis = null;
    }
  }

  function refresh() {
    safeRefresh();
  }

  function scrollTo({ target, ...options }) {
    if (lenis) lenis.scrollTo(target, options);
  }

  // Init on load
  init();

  // Sort pinned triggers and refresh after page hydration
  function onPageEntered() {
    if (ScrollTriggerRef) ScrollTriggerRef.sort();
    safeRefresh();
  }

  // EventBus API
  eventBus.on('smoothScroll:init', init);
  eventBus.on('smoothScroll:reset', reset);
  eventBus.on('smoothScroll:pause', pause);
  eventBus.on('smoothScroll:resume', resume);
  eventBus.on('smoothScroll:destroy', destroy);
  eventBus.on('smoothScroll:refresh', refresh);
  eventBus.on('smoothScroll:scrollTo', scrollTo);
  eventBus.on('page:entered', onPageEntered);

  // Refresh after fonts load — reflow may shift trigger positions
  document.fonts.ready.then(() => safeRefresh());

  // htmx compatibility: refresh after Sprig swaps
  if (typeof htmx !== 'undefined') {
    htmx.on('htmx:afterSwap', () => {
      safeRefresh();
    });
  }

  return () => {
    destroy();
    eventBus.off('smoothScroll:init', init);
    eventBus.off('smoothScroll:reset', reset);
    eventBus.off('smoothScroll:pause', pause);
    eventBus.off('smoothScroll:resume', resume);
    eventBus.off('smoothScroll:destroy', destroy);
    eventBus.off('smoothScroll:refresh', refresh);
    eventBus.off('smoothScroll:scrollTo', scrollTo);
    eventBus.off('page:entered', onPageEntered);
  };
}

export default SmoothScroll;
